import { FeatureFlagService } from 'src/app/modules/feature-flags/services/feature-flag/feature-flag.service';
import { ConfigDataService } from './modules/olp-api/services/config-data/config-data.service';
import { ConfigDataAPI } from './modules/olp-api/classes/config-data/config-data-api';
import { KnowledgeBaseAPI } from './modules/olp-api/classes/knowledge-base/knowledge-base-api';
import { KnowledgeBaseService } from './modules/olp-api/services/kb/kb.service';
import { HeaderModule } from './modules/header/header.module';
import { MessageModule } from './modules/message/message.module';
import { SharedModule } from './shared/shared.module';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  NgModule,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
  httpClientInMemBackendServiceFactory,
  InMemoryBackendConfig,
  InMemoryDbService,
} from 'angular-in-memory-web-api';
import { SimulacrumService } from './shared/services/simulacrum/simulacrum.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CommonModule } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import {
  HttpBackend,
  HttpClient,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { FeatureFlagModule } from './modules/feature-flags/feature-flag.module';
import { AuthConfigModule } from './modules/auth/modules/auth-config.module';
import { SubheaderModule } from './modules/subheader/subheader.module';
import { DebugModule } from './modules/debug/debug.module';
import { FooterModule } from './modules/footer/footer.module';
import { ToolsModule } from './modules/tools/tools.module';
import { ConfigurationService } from './modules/configuration/services/configuration/configuration.service';
import { HTTPInterceptorService } from './modules/http-interceptor/http-interceptor.service';
import {
  MARKED_OPTIONS,
  MarkdownModule,
  MarkedOptions,
  MarkedRenderer,
} from 'ngx-markdown';
import type { Tokens } from 'marked';
import { AccordionModule } from './modules/accordion/accordion.module';
import { AuthInterceptor, OidcSecurityService } from 'angular-auth-oidc-client';
import { AwsCredentialsService } from './shared/services/aws-credentials/aws-credentials.service';
import {
  catchError,
  filter,
  firstValueFrom,
  map,
  switchMap,
  take,
  timeout,
} from 'rxjs';
import { ClickstreamAPI } from './modules/olp-api/classes/clickstream-api/clickstream-api';
import { ClickstreamService } from './modules/olp-api/services/clickstream/clickstream.service';
import { GraphqlModule } from './modules/graphql/graphql.module';
import { AssignmentAPI } from './modules/olp-api/classes/assignment-api/assignment-api';
import { AssignmentService } from './modules/olp-api/services/assignment/assignment.service';
import { LoadingComponent } from './modules/loading-screen/components/loading/loading.component';
import { ExpandableModalComponent } from './modules/expandable-content/components/expanded-modal/expandable-modal.component';
import { ChartComponent } from './modules/chart/chart.component';
import { HubConnectionBuilder } from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { FeatureFlags } from './shared/enums/feature-flags';
import * as Sentry from '@sentry/angular';

const DEFAULT_SENTRY_CONSOLE_INTEGRATION_LEVELS = ['']; // Default levels for console integration

/** Maximum time to wait for OIDC auth to become ready before a credentials fetch gives up. */
const AUTH_READY_TIMEOUT_MS = 30000;

function initializeApp(
  configService: ConfigurationService,
  flagService: FeatureFlagService
): () => Promise<void> {
  return () =>
    new Promise(resolve => {
      configService.load().then(() => {
        flagService.populateFlags();

        if (flagService.isFlagEnabled(FeatureFlags.ENABLE_SENTRY_LOGGING)) {
          Sentry.init({
            dsn: configService.config.sentryDSN,
            environment: configService.config.envShortName,
            release: environment.version,
            sampleRate: configService.config.sentrySampleRate,
            // COPPA/FERPA: do not send client IP address or other default PII to Sentry.
            sendDefaultPii: false,
            ignoreErrors: ['ExpressionChangedAfterItHasBeenCheckedError'],
            debug: configService.config.production ? false : true,
            integrations: [
              Sentry.breadcrumbsIntegration({ console: false, dom: false }),
              Sentry.captureConsoleIntegration({
                levels:
                  configService.config.sentryConsoleIntegrationLevels ||
                  DEFAULT_SENTRY_CONSOLE_INTEGRATION_LEVELS,
              }),
            ],
          });
        }

        resolve();
      });
    });
}

function markedOptionsFactory(): MarkedOptions {
  const renderer = new MarkedRenderer();

  renderer.link = function ({ text }: Tokens.Link): string {
    return text;
  };

  return {
    renderer: renderer,
  };
}

/**
 * Wait for auth to be ready and then return the id_token so that startup
 * queries block on auth instead of racing it and failing.
 */
export function createIdTokenProvider(
  oidcSecurityService: OidcSecurityService
): () => Promise<string> {
  return () =>
    firstValueFrom(
      oidcSecurityService.isAuthenticated$.pipe(
        filter(({ isAuthenticated }) => isAuthenticated),
        take(1),
        timeout(AUTH_READY_TIMEOUT_MS),
        // Force a refresh to get a new id_token
        switchMap(() =>
          oidcSecurityService.forceRefreshSession().pipe(
            // Bound the refresh so that a stalled request can't block callers
            timeout(AUTH_READY_TIMEOUT_MS),
            map(({ idToken }) => {
              // Treat a missing id_token as a failure so that it hits the fallback below
              if (!idToken) {
                throw new Error('Forced refresh returned no id_token');
              }
              return idToken;
            }),
            // Fall back on the stored id_token if the forced refresh fails
            catchError(error => {
              console.warn(
                'Forced id_token refresh failed. Falling back on stored id_token.',
                error
              );
              return oidcSecurityService.getIdToken();
            })
          )
        )
      )
    ).then(token => token ?? '');
}

@NgModule({
  declarations: [AppComponent],
  exports: [TranslateModule],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    CommonModule,
    AngularSvgIconModule.forRoot(),
    BrowserAnimationsModule,
    MessageModule,
    HeaderModule,
    SubheaderModule,
    TranslateModule.forRoot({
      // example translation file location:   /assets/i18n/page-prompts/en.json
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) =>
          new TranslateHttpLoader(http, '/assets/i18n/page-prompts/', '.json'),
        deps: [HttpClient],
      },
    }),
    AuthConfigModule,
    FeatureFlagModule,
    DebugModule,
    FooterModule,
    ToolsModule,
    MarkdownModule.forRoot({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: markedOptionsFactory,
      },
    }),
    AccordionModule,
    ChartComponent,
    GraphqlModule,
    LoadingComponent,
    ExpandableModalComponent,
  ],
  providers: [
    provideZoneChangeDetection(),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [ConfigurationService, FeatureFlagService],
      multi: true,
    },
    {
      // Shared credentials service for IAM/SigV4 AppSync auth. Constructed
      // post-bootstrap, so it reads runtime configuration.
      provide: AwsCredentialsService,
      useFactory: (
        configService: ConfigurationService,
        oidcSecurityService: OidcSecurityService
      ): AwsCredentialsService =>
        new AwsCredentialsService(
          {
            get identityPoolId(): string {
              return configService.config.appSyncIdentityPoolId ?? '';
            },
            get idsrvHost(): string {
              return new URL(configService.config.idServerUrl).hostname;
            },
          },
          createIdTokenProvider(oidcSecurityService)
        ),
      deps: [ConfigurationService, OidcSecurityService],
    },
    KnowledgeBaseService,
    KnowledgeBaseAPI,
    ConfigDataAPI,
    ConfigDataService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HTTPInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        logErrors: true,
      }),
    },
    ClickstreamAPI,
    ClickstreamService,
    AssignmentAPI,
    AssignmentService,
    HubConnectionBuilder,
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: InMemoryBackendConfig,
      useValue: new InMemoryBackendConfig({
        dataEncapsulation: false,
        passThruUnknownUrl: true,
      }),
    },
    {
      provide: InMemoryDbService,
      useExisting: SimulacrumService,
    },
    {
      provide: HttpBackend,
      useFactory: httpClientInMemBackendServiceFactory,
    },
  ],
})
export class AppModule {}
